import React, { useEffect, useState } from "react";

const GoogleG = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-label="Google" role="img">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const Star = ({ filled = true }: { filled?: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth={filled ? 0 : 1.5} aria-hidden="true">
    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);

const AVATAR_COLORS = ["#1a73e8", "#0f9d58", "#db4437", "#7e3ff2", "#f4a000", "#0b8043", "#e91e63", "#00838f"];
const colorFor = (name: string) => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
const initialOf = (name: string) => name.trim().charAt(0).toUpperCase();

const Stars = ({ rating }: { rating: number }) => {
  const filled = Math.round(rating);
  return (
    <div className="testimonial-stars">
      <span className="sr-only">Rated {rating} out of 5 stars</span>
      {Array.from({ length: 5 }, (_, i) => <Star key={i} filled={i < filled} />)}
    </div>
  );
};

interface StaticTestimonial { name: string; color: string; time: string; text: string; }

const STATIC_REVIEWS: StaticTestimonial[] = [
  { name: "Michael Chen",    color: "#1a73e8", time: "2 weeks ago",  text: "J&S handled our conference room from end to end. Video conferencing, displays, cabling. Everything is clean and it just works. Their follow-up has been excellent." },
  { name: "Sarah O'Brien",   color: "#0f9d58", time: "a month ago",  text: "We had them design a dedicated home theater from scratch. The display and acoustic treatment are stunning, and they walked us through every decision without any pressure." },
  { name: "Daniel Reyes",    color: "#db4437", time: "2 months ago", text: "Distributed audio across our restaurant with perfect zone control. The team was respectful, on time, and the staff actually understands how to use the system now." },
  { name: "Jennifer Walsh",  color: "#7e3ff2", time: "2 months ago", text: "Our outdoor patio sound is incredible. They took the time to plan speaker placement around the landscaping and the result is even and unobtrusive." },
  { name: "Robert Anastasi", color: "#f4a000", time: "3 months ago", text: "Genuinely the most communicative AV company I've worked with. They came back twice to dial things in after install at no additional charge. Top notch." },
  { name: "Emily Tran",      color: "#0b8043", time: "4 months ago", text: "Wall mounted three televisions across our home. The wiring is invisible, the brackets are flush, and they cleaned up perfectly. Highly recommend J&S." },
];

interface GoogleReview {
  author_name: string;
  rating: number;
  relative_time_description: string;
  text: string;
}

interface LiveData {
  available: true;
  rating: number;
  reviewCount: number;
  googleUrl: string;
  reviews: GoogleReview[];
}

type ReviewState =
  | { status: "loading" }
  | { status: "live"; data: LiveData }
  | { status: "fallback" };

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

const Testimonials: React.FC = () => {
  const [state, setState] = useState<ReviewState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/reviews`)
      .then((r) => r.json())
      .then((data: { available: boolean } & Partial<LiveData>) => {
        if (cancelled) return;
        if (data.available && data.rating != null && data.reviewCount != null && data.googleUrl && data.reviews) {
          setState({ status: "live", data: data as LiveData });
        } else {
          setState({ status: "fallback" });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ status: "fallback" });
      });
    return () => { cancelled = true; };
  }, []);

  const isLive = state.status === "live";
  const liveData = isLive ? state.data : null;

  const cards = isLive
    ? liveData!.reviews.map((r) => ({
        name: r.author_name,
        color: colorFor(r.author_name),
        time: r.relative_time_description,
        text: r.text,
        rating: r.rating,
      }))
    : state.status === "fallback"
    ? STATIC_REVIEWS.map((r) => ({ ...r, rating: 5 }))
    : [];

  return (
    <section className="testimonials" data-testid="testimonials">
      <div className="container">
        <div className="center">
          <span className="eyebrow">What they said</span>
          <h2>Kind words from our customers</h2>
          {isLive && liveData && (
            <div className="reviews-aggregate" data-testid="reviews-aggregate">
              <div className="reviews-aggregate-stars">
                <span className="sr-only">Rated {liveData.rating} out of 5 on Google</span>
                {Array.from({ length: 5 }, (_, i) => <Star key={i} filled={i < Math.round(liveData.rating)} />)}
              </div>
              <span className="reviews-aggregate-score">{liveData.rating.toFixed(1)}</span>
              <span className="reviews-aggregate-count">({liveData.reviewCount.toLocaleString()} reviews)</span>
              <a
                href={liveData.googleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="reviews-aggregate-link"
                data-testid="link-google-reviews"
              >
                Read all reviews on Google
              </a>
            </div>
          )}
        </div>

        {state.status === "loading" && (
          <div className="testimonial-grid" role="status" aria-busy="true">
            <span className="sr-only">Loading reviews</span>
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="testimonial-card testimonial-skeleton" aria-hidden="true" />
            ))}
          </div>
        )}

        {state.status !== "loading" && (
          <div className="testimonial-grid">
            {cards.map((t) => (
              <article className="testimonial-card" key={`${t.name}-${t.time}`} data-testid="testimonial-card">
                <div className="testimonial-head">
                  <div className="testimonial-avatar" style={{ background: t.color }} aria-hidden="true">{initialOf(t.name)}</div>
                  <div style={{ flex: 1 }}>
                    <div className="testimonial-name">{t.name}</div>
                    <div className="testimonial-time">{t.time}</div>
                  </div>
                  <GoogleG />
                </div>
                <Stars rating={t.rating} />
                <p className="testimonial-quote">{t.text}</p>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
